import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './BlogManager.css';
import { ArrowLeft, Eye, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { blogService } from '../../../services';

const BlogManager = () => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);

    const fetchPosts = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            try {
                const adminPosts = await blogService.getAdminPosts();
                setPosts(adminPosts);
                setIsAdmin(true);
                return;
            } catch {
                const myPosts = await blogService.getMyPosts();
                setPosts(myPosts);
                setIsAdmin(false);
            }
        } catch (fetchError) {
            console.error('Error loading blog posts:', fetchError);
            setError('Unable to load blog posts.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const filteredPosts = useMemo(() => {
        const term = searchQuery.trim().toLowerCase();
        if (!term) return posts;

        return posts.filter((post) =>
            [post.title, post.excerpt, post.category, post.status, post.authorName]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(term))
        );
    }, [posts, searchQuery]);

    const handleDelete = async (postId) => {
        if (!window.confirm('Delete this blog post?')) return;

        try {
            await blogService.deletePost(postId);

            setPosts((current) => current.filter((post) => post.postId !== postId));
        } catch (deleteError) {
            console.error('Error deleting blog post:', deleteError);
            setError('Unable to delete blog post.');
        }
    };

    const handlePreview = (slug) => {
        if (!slug) return;
        window.open(`/dashboard/blog/${slug}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="blogmanager-page">
            <div className="blogmanager-header">
                <button className="blogmanager-back" onClick={() => navigate('/dashboard/blog')}>
                    <ArrowLeft size={18} />
                    Back to Blog
                </button>
                <div className="blogmanager-title">
                    <h1>Manage Blog</h1>
                    <p>{isAdmin ? 'All blog posts across the platform' : 'Your blog posts'}</p>
                </div>
                <button className="blogmanager-create" onClick={() => navigate('/dashboard/blog/editor')}>
                    <Plus size={18} />
                    New Post
                </button>
            </div>

            {error ? <div className="blogmanager-error">{error}</div> : null}

            <div className="blogmanager-toolbar">
                <div className="blogmanager-summary">
                    <div className="blogmanager-stat">
                        <span>Total posts</span>
                        <strong>{posts.length}</strong>
                    </div>
                    <div className="blogmanager-stat">
                        <span>Published</span>
                        <strong>{posts.filter((post) => post.status === 'Published').length}</strong>
                    </div>
                    <div className="blogmanager-stat">
                        <span>Drafts</span>
                        <strong>{posts.filter((post) => post.status === 'Draft').length}</strong>
                    </div>
                </div>
                <div className="blogmanager-controls">
                    <input
                        type="text"
                        placeholder="Search title, category, status..."
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                    />
                    <div className="blogmanager-scope-label">
                        {isAdmin ? 'Admin view: all posts' : 'Contributor view: your posts'}
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="blogmanager-empty">
                    <FileText size={48} />
                    <h3>Loading blog posts...</h3>
                </div>
            ) : filteredPosts.length === 0 ? (
                <div className="blogmanager-empty">
                    <FileText size={48} />
                    <h3>{posts.length ? 'No posts match that search' : 'No blog posts yet'}</h3>
                    <p>{posts.length ? 'Try a broader search.' : 'Create your first blog post to start building the blog.'}</p>
                </div>
            ) : (
                <div className="blogmanager-list">
                    {filteredPosts.map((post) => (
                            <div key={post.postId} className="blogmanager-card">
                                <div className="blogmanager-card-main">
                                    <div className="blogmanager-card-meta">
                                        <span className={`blogmanager-badge status-${(post.status || 'Draft').toLowerCase()}`}>{post.status || 'Draft'}</span>
                                        <span className="blogmanager-badge">{post.visibility || 'Public'}</span>
                                        {post.isFeatured ? <span className="blogmanager-badge featured">Featured</span> : null}
                                    </div>
                                    <h3>{post.title}</h3>
                                    <p>{post.excerpt || 'No excerpt yet.'}</p>
                                    <div className="blogmanager-card-details">
                                        <span>{post.category || 'Uncategorized'}</span>
                                        <span>{post.authorDisplayName || post.authorName || 'Unknown author'}</span>
                                        <span>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-GB') : 'Unpublished'}</span>
                                    </div>
                                </div>
                                <div className="blogmanager-card-actions">
                                    <button onClick={() => handlePreview(post.slug)}>
                                        <Eye size={16} />
                                        Preview
                                    </button>
                                    <button onClick={() => navigate(`/dashboard/blog/editor/${post.postId}`)}>
                                        <Pencil size={16} />
                                        Edit
                                    </button>
                                    <button className="danger" onClick={() => handleDelete(post.postId)}>
                                        <Trash2 size={16} />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};

export default BlogManager;
